import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

type TextBlock =
    | { type: 'heading'; text: string; level: number }
    | { type: 'paragraph'; text: string }
    | { type: 'unordered-list'; items: string[] }
    | { type: 'ordered-list'; items: string[] };

export const parseFormattedText = (value: string): TextBlock[] => {
    const lines = value.replace(/\r\n/g, '\n').split('\n');
    const blocks: TextBlock[] = [];

    let paragraphLines: string[] = [];
    let listType: 'unordered-list' | 'ordered-list' | null = null;
    let listItems: string[] = [];

    const flushParagraph = () => {
        const text = paragraphLines.join(' ').trim();
        if (text) blocks.push({ type: 'paragraph', text });
        paragraphLines = [];
    };

    const flushList = () => {
        if (listType && listItems.length > 0) {
            blocks.push({ type: listType, items: [...listItems] });
        }
        listType = null;
        listItems = [];
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
            flushParagraph();
            flushList();
            continue;
        }

        if (/^[-–—=─*_~]{2,}$/.test(line)) {
            flushParagraph();
            flushList();
            continue;
        }

        const markdownHeading = line.match(/^(#{1,6})\s+(.+)$/);
        if (markdownHeading) {
            flushParagraph();
            flushList();
            blocks.push({
                type: 'heading',
                level: markdownHeading[1].length,
                text: markdownHeading[2].trim(),
            });
            continue;
        }

        const colonHeading = line.match(/^([A-Za-z0-9ÇĞİÖŞÜçğıöşü\s\-\/]{3,80}):$/);
        if (colonHeading) {
            flushParagraph();
            flushList();
            blocks.push({
                type: 'heading',
                level: 3,
                text: colonHeading[1].trim(),
            });
            continue;
        }

        const unorderedItem = line.match(/^[-*•]\s+(.+)$/);
        if (unorderedItem) {
            flushParagraph();
            if (listType && listType !== 'unordered-list') flushList();
            listType = 'unordered-list';
            listItems.push(unorderedItem[1].trim());
            continue;
        }

        const orderedItem = line.match(/^\d+[.)]\s+(.+)$/);
        if (orderedItem) {
            flushParagraph();
            if (listType && listType !== 'ordered-list') flushList();
            listType = 'ordered-list';
            listItems.push(orderedItem[1].trim());
            continue;
        }

        flushList();
        paragraphLines.push(line);
    }

    flushParagraph();
    flushList();
    return blocks;
};

const renderInlineMarkdown = (raw: string): ReactNode => {
    const parts: ReactNode[] = [];
    let remaining = raw;
    let keyIdx = 0;

    while (remaining.length > 0) {
        const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
        const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

        let earliest: { idx: number; len: number; node: ReactNode } | null = null;

        if (boldMatch && boldMatch.index !== undefined) {
            earliest = {
                idx: boldMatch.index,
                len: boldMatch[0].length,
                node: <strong key={`b${keyIdx++}`}>{boldMatch[1]}</strong>,
            };
        }

        if (italicMatch && italicMatch.index !== undefined) {
            const isInsideBold = earliest && italicMatch.index >= earliest.idx && italicMatch.index < earliest.idx + earliest.len;
            if (!isInsideBold && (!earliest || italicMatch.index < earliest.idx)) {
                earliest = {
                    idx: italicMatch.index,
                    len: italicMatch[0].length,
                    node: <em key={`i${keyIdx++}`}>{italicMatch[1]}</em>,
                };
            }
        }

        if (!earliest) {
            parts.push(remaining);
            break;
        }

        if (earliest.idx > 0) {
            parts.push(remaining.slice(0, earliest.idx));
        }
        parts.push(earliest.node);
        remaining = remaining.slice(earliest.idx + earliest.len);
    }

    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
};

export const renderInlineMarkdownToHtml = (raw: string): string =>
    raw
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

const FormattedLlmText = ({ text }: { text: string }) => {
    const blocks = parseFormattedText(text);

    if (!blocks.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                İçerik bulunamadı.
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {blocks.map((block, index) => {
                if (block.type === 'heading') {
                    const isMain = block.level <= 2;
                    return (
                        <Typography
                            key={`${block.type}-${index}`}
                            variant={isMain ? 'subtitle1' : 'subtitle2'}
                            sx={{
                                fontWeight: isMain ? 800 : 700,
                                color: '#0D3D4A',
                                mt: index === 0 ? 0 : 0.5,
                            }}
                        >
                            {renderInlineMarkdown(block.text)}
                        </Typography>
                    );
                }

                if (block.type === 'paragraph') {
                    return (
                        <Typography
                            key={`${block.type}-${index}`}
                            variant="body2"
                            sx={{ color: '#23444E', lineHeight: 1.65, whiteSpace: 'normal' }}
                        >
                            {renderInlineMarkdown(block.text)}
                        </Typography>
                    );
                }

                const isOrdered = block.type === 'ordered-list';
                return (
                    <Box
                        key={`${block.type}-${index}`}
                        component={isOrdered ? 'ol' : 'ul'}
                        sx={{
                            m: 0,
                            pl: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            color: '#23444E',
                        }}
                    >
                        {block.items.map((item, itemIndex) => (
                            <Box component="li" key={`${block.type}-${index}-${itemIndex}`}>
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#23444E' }}>
                                    {renderInlineMarkdown(item)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
};

export default FormattedLlmText;
